import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { debounceTime, finalize, Subscription, switchMap, take } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthComponentState } from '../../components/auth/auth.component';
import { EventTypeRadioInputComponent } from '../../components/event-type-radio-input/event-type-radio-input.component';
import { Currency } from '../../enums/price/currencies.enum';
import { IEventPostData } from '../../models/event/event-post-data.model';
import { AuthService } from '../../services/auth.service';
import { EventPostResultType, EventsService } from '../../services/entities/events/events.service';
import { AdminPostRegistrationWithEventResultType, UsersService } from '../../services/entities/users/users.service';
import { ModalService } from '../../services/modal.service';
import { SelectedOrgService } from '../../services/selected-org.service';
import { UserStoreService } from '../../services/store/user-store.service';
import { UtilsService } from '../../services/utils.service';
import { AuthModalComponent } from '../auth-modal/auth-modal.component';
import { ModalHeaderComponent } from '../components/modal-header/modal-header.component';
import { Modal } from '../modal';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Language } from '../../enums/utils/languages.enum';
import { emailValidator } from '../../utils/session-group-form-utils/validators';
import { BlankInputComponent } from '../../components/blank-input/blank-input.component';
import { TabViewModule } from 'primeng/tabview';
import { EventFormat } from '../../models/event/event.model';
import { InputTextSubtleComponent } from '../../components/subtle-inputs/input-text-subtle/input-text-subtle.component';
import { InputTextareaSubtleComponent } from '../../components/subtle-inputs/input-textarea-subtle/input-textarea-subtle.component';

enum Tabs {
  GENERAL = 0,
  CUSTOM = 1,
}

interface IForm {
  title: FormControl<string>;
  currencies: FormControl<Currency[]>;
  email?: FormControl<string>;
  format: FormControl<EventFormat>;
};

// todo   mel by se zobrazovat dropdown do ktere organizace to chce založit (ted se bere z selectedOrgId -
// v pripade kdy jich bude mit vic muze nastavat zmatení)

@Component({
  selector: 'app-new-event-modal',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, TranslateModule,
    MultiSelectModule, ButtonModule, InputTextModule, TabViewModule,
    ModalHeaderComponent, EventTypeRadioInputComponent, TranslateModule, InputTextareaSubtleComponent,
  ],
  templateUrl: './new-event-modal.component.html',
  styleUrls: ['./new-event-modal.component.scss']
})
export class NewEventModalComponent extends Modal implements OnInit, OnDestroy {
  Env = environment;
  Tabs = Tabs;

  @Input() prefillTitle: string | undefined;
  @Input() prefillFormat: EventFormat | undefined;
  @Output() eventCreated: EventEmitter<void> = new EventEmitter();

  initEmailFC = new FormControl('', { nonNullable: true, validators: [ Validators.required, emailValidator ]});
  form = new FormGroup<IForm>({
    title: new FormControl('', { nonNullable: true, validators: [ Validators.required ]}),
    currencies: new FormControl([], { nonNullable: true, validators: [ Validators.required ]}),
    format: new FormControl(EventFormat.EVENT, { nonNullable: true, validators: [ Validators.required ]})
  });

  availableCurrencies: Currency[] = Object.values(Currency);
  urlId: string | undefined;

  tabActiveIndex: number = 0;

  titleSub: Subscription = Subscription.EMPTY;
  userSub: Subscription = Subscription.EMPTY;

  fetchingUrlId: boolean = false;
  creatingEvent: boolean = false;

  constructor(
    private utilsService: UtilsService,
    private eventsService: EventsService,
    private userStore: UserStoreService,
    private usersService: UsersService,
    private selectedOrgService: SelectedOrgService,
    private modalService: ModalService,
    private authService: AuthService,
    private translate: TranslateService
  ) {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.watchTitle();

    if (this.prefillTitle) this.form.patchValue({ title: this.prefillTitle });
    if (this.prefillFormat) this.form.patchValue({ format: this.prefillFormat });

    this.watchUser();
  }

  watchUser() {
    this.userSub = this.userStore.state.user$.subscribe(user => {
      if (user) {
        this.form.removeControl('email');
      } else {
        this.form.addControl('email', this.initEmailFC);
      }
    });
  }

  watchTitle() {
    // watch title and find url id
    this.titleSub = this.titleFC?.valueChanges.pipe(
      debounceTime(300),
      switchMap(vals => {
        this.fetchingUrlId = true;
        return this.eventsService.getUniqueUrlId(vals);
      })
    ).subscribe(urlId => {
      this.fetchingUrlId = false;
      this.urlId = urlId;
    }) || Subscription.EMPTY;
  }

  onCreate() {
    this.utilsService.markFormGroupDirty(this.form);
    if (this.form.invalid || this.creatingEvent || this.fetchingUrlId) {
      console.error('invalid')
      return;
    }

    const orgId = this.selectedOrgService.getCurrentValue();
    const formVals = this.form.getRawValue();
    const data: IEventPostData = {
      title: formVals.title,
      currencies: formVals.currencies,
      email: formVals.email,
      organizationId: orgId ? orgId : undefined,
      format: formVals.format,
      userPreferredLanguage: this.translate.currentLang as Language,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    this.creatingEvent = true;
    // user signedin - create event
    if (this.authService.isAuthenticated()) {
      this.eventsService.postEvent(data).pipe(
        take(1),
        finalize(() => this.creatingEvent = false)
      ).subscribe({
        next: (res) => {
          switch (res.resultType) {
            case EventPostResultType.EVENT_ADDED_TO_NEW_ORGANIZATION:
              if (res.newOrganizationId) this.selectedOrgService.setOrgId(res.newOrganizationId, false);
              this.eventCreated.emit();
              this.close();
              break;
            case EventPostResultType.EVENT_ADDED_TO_SPECIFIED_ORGID:
              this.eventCreated.emit();
              this.close();
              break;
            default:
              console.error('Unhandled state! 12lk3m1.,a');
              break;
          }
        }
      });
    }
    // user not signed in - create user, event, org, ...
    else {
      this.usersService.postRegistrationWithEvent(data).pipe(
        take(1),
        finalize(() => this.creatingEvent = false)
      ).subscribe({
        next: (res) => {
          switch (res.resultType) {
            case AdminPostRegistrationWithEventResultType.NEW_USER_CREATED:
              this.authService.signInWithToken(res.signInToken!).then(() => {
                this.close();
              });
              this.eventCreated.emit();
              break;
            case AdminPostRegistrationWithEventResultType.SIGN_IN:
              this.modalService.openAuthModal({
                c: AuthModalComponent,
                title: "admin.authmodal.login.title",
                initEmail: data.email,
                state: AuthComponentState.SIGN_IN
              });
              break;
            default:
              this.utilsService.logError('Should not happen! 9iuowpeup;oewqijdoi');
              break;
          }
        }
      });
    }

  }
  
  onTabIndexChange(index: number) {
    this.tabActiveIndex = index;
  }

  get titleFC() {
    return this.form.get('title');
  }
  get emailFC() {
    return this.form.get('email');
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    
    this.titleSub.unsubscribe();
    this.userSub.unsubscribe();
  }
}
